import { ModalClose } from '@startuptools/ui/ModalClose';
import { Modal } from '@startuptools/ui/Modal';
import { ModalDialog } from '@startuptools/ui/ModalDialog';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Trans } from '@lingui/react';
import { type SubmitHandler } from 'react-hook-form';
import { Button } from '@startuptools/ui/Button';
import { GqlMeetingHeldKind, GqlMeetingKind } from '../../../../graphql/base-types.graphql';
import { useCreateMeetingDialogSuspenseQuery } from './CreateMeetingDialog.graphql';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { type FormValues, MeetingDetailsForm } from './MeetingDetailsForm';
import { DateTime } from 'luxon';
import {
  type DateSettings,
  getDefaultMeetingNumber,
  getDefaultPlace,
  getDefaultStartTime,
} from './create-meeting.utils';
import { isPromise } from '../../../../helpers/is-promise';
import React from 'react';
import { ErrorBoundary } from '@startuptools/ui/ErrorBoundary';

type Props = {
  onClose: () => void;
  meetingKind: GqlMeetingKind;
  usesStepper: boolean;
  onSubmit: SubmitHandler<FormValues>;
  dateSettings?: DateSettings;
};

const CreateMeetingDialogInner = ({ onClose, meetingKind, usesStepper, onSubmit, dateSettings }: Props) => {
  const companyId = useCompanyId();

  const {
    data: {
      company,
      meetings: { models: meetings },
    },
  } = useCreateMeetingDialogSuspenseQuery({
    variables: {
      companyId,
      meetingKinds:
        meetingKind === GqlMeetingKind.BoardMeeting
          ? [GqlMeetingKind.BoardMeeting]
          : [GqlMeetingKind.ShareholdersMeeting, GqlMeetingKind.ExtraShareholdersMeeting],
    },
    fetchPolicy: 'no-cache',
  });

  const lastMeeting = meetings[0];
  const lastMeetingStartTime = lastMeeting ? DateTime.fromISO(lastMeeting.startTime) : undefined;

  const handleSubmit: SubmitHandler<FormValues> = async values => {
    const res = onSubmit(values);
    if (isPromise(res)) {
      await res;
    }
    onClose();
  };

  return (
    <MeetingDetailsForm
      company={company}
      onSubmit={handleSubmit}
      meetings={meetings}
      formDefaults={{
        number: getDefaultMeetingNumber(lastMeeting?.number),
        heldKind: GqlMeetingHeldKind.Physical,
        place: getDefaultPlace(company, lastMeeting),
        phone: lastMeeting?.phone ?? null,
        startTime: getDefaultStartTime(company, meetingKind, lastMeetingStartTime, dateSettings),
        endTime: null,
      }}
      meetingKind={meetingKind}
      usesStepper={usesStepper}
      isEditing={false}
      actions={loading => (
        <Stack flexGrow={1} justifyContent="end">
          <Stack direction="row" gap={1} justifyContent="end">
            <Button variant="outlined" color="neutral" onClick={onClose} disabled={loading}>
              <Trans id="Cancel" />
            </Button>
            <Button type="submit" loading={loading}>
              <Trans id="Create Meeting" />
            </Button>
          </Stack>
        </Stack>
      )}
    />
  );
};

export const CreateMeetingDialog = ({ onClose, meetingKind, usesStepper, onSubmit, dateSettings }: Props) => {
  return (
    <Modal open onClose={onClose}>
      <ModalDialog sx={{ width: 532, maxHeight: '90vh', overflow: 'auto' }}>
        <ModalClose />
        <Stack gap={2} flexGrow={1}>
          <Typography level="h4">
            <Trans id="Create Meeting" />
          </Typography>
          <React.Suspense>
            <ErrorBoundary>
              <CreateMeetingDialogInner
                onClose={onClose}
                meetingKind={meetingKind}
                usesStepper={usesStepper}
                onSubmit={onSubmit}
                dateSettings={dateSettings}
              />
            </ErrorBoundary>
          </React.Suspense>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};
